import { z } from 'zod';
import { compararDinheiro, somarDinheiro, subtrairDinheiro } from './aritmetica.ts';
import { dinheiroSchema } from './comum.ts';

/**
 * O saldo a faturar por nota de serviço — a "nota parcial" (`.spec/015`,
 * decisão 94).
 *
 * A obra e o contrato têm um valor no cadastro, e a nota de serviço fatura
 * esse valor **aos poucos**: uma medição por nota. O saldo a faturar de cada um
 * é o valor do cadastro menos o que as notas já faturaram, e é ele que a
 * emissão confere.
 *
 * Quatro respostas do Adriano, de 07/10/2026, estão neste arquivo:
 *
 * 1. **Obra e contrato, cada um com o seu.** A nota abate o saldo da obra que
 *    ela aponta e o do contrato que ela aponta. São dois saldos, e não um.
 * 2. **O que abate é o serviço menos o desconto incondicionado** —
 *    `valorFaturadoDaNotaServico`. Nem o bruto, nem o líquido.
 * 3. **Passar do saldo é parâmetro da conta**: bloquear ou alertar.
 * 4. **Sem valor no cadastro, fatura livre.** O contrato por medição não tem
 *    valor fechado, e não há saldo a controlar: mostra-se só o já faturado.
 *
 * Arquivo próprio, e não um trecho de `notas-servico.ts`, porque a política é
 * lida também por `conta.ts` — e `notas-servico.ts` já importa o bastante para
 * que um ciclo entre os dois fosse questão de tempo.
 */

/* -------------------------------------------------------------------------- */
/* A política da conta                                                        */
/* -------------------------------------------------------------------------- */

/**
 * O que a emissão faz quando a nota passa do saldo a faturar.
 *
 * As mesmas duas palavras da política de pedido acima do solicitado
 * (`politicaPedidoExcedenteSchema`), para o administrador não aprender dois
 * vocabulários na mesma tela. Não há `TOLERANCIA` aqui: a folga do pedido
 * existe porque cimento vem em palete fechado, e faturamento não tem
 * embalagem — quem precisa cobrar a mais ajusta o valor do cadastro.
 */
export const POLITICAS_FATURAMENTO_EXCEDENTE = ['BLOQUEAR', 'ALERTAR'] as const;
export const politicaFaturamentoExcedenteSchema = z.enum(POLITICAS_FATURAMENTO_EXCEDENTE);
export type PoliticaFaturamentoExcedente = z.infer<typeof politicaFaturamentoExcedenteSchema>;

/**
 * A política de toda conta que nunca abriu a configuração.
 *
 * `BLOQUEAR` porque é o erro mais barato: a nota recusada fica em rascunho e a
 * mensagem diz o saldo; a nota emitida a mais só se desfaz cancelando na
 * prefeitura.
 */
export const POLITICA_FATURAMENTO_EXCEDENTE_PADRAO: PoliticaFaturamentoExcedente = 'BLOQUEAR';

export const ROTULO_POLITICA_FATURAMENTO_EXCEDENTE: Readonly<
  Record<PoliticaFaturamentoExcedente, string>
> = {
  BLOQUEAR: 'Bloquear',
  ALERTAR: 'Alertar e permitir',
};

/**
 * O que cada política faz, na palavra do usuário.
 *
 * **Estes textos são promessa**, como os de `DESCRICAO_POLITICA_EXCEDENTE`:
 * quem escolhe está decidindo pelo que está escrito aqui. Ao mexer na frase,
 * mexa no comportamento primeiro.
 */
export const DESCRICAO_POLITICA_FATURAMENTO_EXCEDENTE: Readonly<
  Record<PoliticaFaturamentoExcedente, string>
> = {
  BLOQUEAR:
    'A nota que passa do saldo a faturar da obra ou do contrato não é emitida: fica em rascunho, e a mensagem diz o saldo. Para faturar a mais — aditivo, reajuste —, ajuste antes o valor no cadastro da obra ou do contrato.',
  ALERTAR:
    'A nota que passa do saldo a faturar é emitida mesmo assim. O diálogo de emissão mostra de quanto ela passa antes de confirmar, e o saldo da obra ou do contrato fica negativo.',
};

/* -------------------------------------------------------------------------- */
/* A conta                                                                    */
/* -------------------------------------------------------------------------- */

/**
 * Quanto uma nota **fatura**: o serviço menos o desconto incondicionado.
 *
 * É o que o cliente passa a dever por aquela medição, antes de reter imposto.
 * As outras duas leituras foram oferecidas e recusadas:
 *
 * - o **valor do serviço** sozinho contaria como faturado um desconto que o
 *   cliente nunca vai pagar, e o saldo fecharia antes do dinheiro;
 * - o **valor líquido** tiraria do faturado o imposto retido — que o cliente
 *   paga do mesmo jeito, só que ao fisco —, e o saldo nunca fecharia numa obra
 *   com retenção de INSS.
 *
 * A **dedução** não entra: ela reduz a base do ISS, e não o que se cobra.
 */
export function valorFaturadoDaNotaServico(nota: {
  valorServico: string;
  valorDescontoIncondicionado: string;
}): string {
  return subtrairDinheiro(nota.valorServico || '0', nota.valorDescontoIncondicionado || '0');
}

/**
 * O saldo a faturar: o valor do cadastro menos o já faturado.
 *
 * `null` quando o cadastro **não tem valor** — contrato por medição, obra sem
 * valor a faturar. Não é zero: zero é "não cabe mais nada", e `null` é "não há
 * teto". **Pode ser negativo**, na conta que alerta e permite, e também quando
 * o valor do cadastro foi reduzido depois de faturado.
 */
export function saldoAFaturar(valor: string | null, faturado: string): string | null {
  return valor === null ? null : subtrairDinheiro(valor, faturado);
}

/**
 * De quanto a nota **passa** do saldo — ou `null` quando cabe.
 *
 * Sem saldo (`null`) nada passa: é a fatura livre do cadastro sem valor. A
 * nota de valor igual ao saldo cabe — ela o zera.
 */
export function excedenteDoSaldo(saldo: string | null, valorDaNota: string): string | null {
  if (saldo === null) return null;

  return compararDinheiro(valorDaNota, saldo) > 0 ? subtrairDinheiro(valorDaNota, saldo) : null;
}

/* -------------------------------------------------------------------------- */
/* Leitura                                                                    */
/* -------------------------------------------------------------------------- */

/** O saldo de **um** cadastro — a obra ou o contrato. */
export const saldoAFaturarSchema = z.object({
  /** Como o cadastro se chama numa frase: o código da obra, o número do contrato. */
  rotulo: z.string(),
  /** O valor do cadastro. `null` é o cadastro sem valor: fatura livre. */
  valor: dinheiroSchema.nullable(),
  /**
   * O que as notas **emitidas** que geraram contas a receber já faturaram.
   *
   * Rascunho, rejeitada e cancelada não contam, e a nota de operação sem
   * financeiro também não: "faturou" é ter título, a mesma leitura da recusa
   * cruzada (`.spec/015`, decisão 90).
   */
  faturado: dinheiroSchema,
  /** `valor − faturado`. `null` sem valor no cadastro; negativo se já passou. */
  saldo: dinheiroSchema.nullable(),
  /**
   * O cadastro **gerou as próprias contas a receber** — o botão da obra, ou as
   * parcelas do contrato (`.spec/015`, decisão 96).
   *
   * Quando é `true`, `saldo` não é "a faturar": o valor inteiro já virou
   * parcela, e a nota com financeiro é recusada pela recusa cruzada (decisão
   * 90) antes de o saldo importar. O número continua vindo — é `valor − 0` —,
   * e a marca é o que impede a tela de mostrá-lo como o que falta cobrar, e a
   * emissão de somar à recusa cruzada um "acima do saldo" que não explica nada.
   *
   * Cada cadastro pela **própria** geração: a obra pelos títulos de origem
   * `OBRA`, o contrato pelos de origem `CONTRATO`. A obra de um contrato que
   * gerou parcelas não vem marcada — quem vem é a linha do contrato, ao lado.
   */
  faturouPeloCadastro: z.boolean(),
});

export type SaldoAFaturar = z.infer<typeof saldoAFaturarSchema>;

/**
 * Os dois saldos que uma nota abate, e a política da conta.
 *
 * `obra` e `contrato` são `null` quando a nota não aponta um — ou quando o
 * contrato apontado não é de recebimento, que não tem o que faturar.
 *
 * A política vem junto porque é ela que decide a frase da tela: "não será
 * emitida" ou "será emitida mesmo assim". Sem ela a tela teria de pedir a
 * configuração da conta, que é `admin.conta.ver` — e quem emite nota não a tem.
 */
export const saldosAFaturarSchema = z.object({
  politica: politicaFaturamentoExcedenteSchema,
  obra: saldoAFaturarSchema.nullable(),
  contrato: saldoAFaturarSchema.nullable(),
});

export type SaldosAFaturar = z.infer<typeof saldosAFaturarSchema>;

/** Os filtros do `GET …/saldo-a-faturar`: a obra e o contrato que a tela escolheu. */
export const saldosAFaturarQuerySchema = z
  .object({
    obraId: z.string().uuid().optional(),
    contratoId: z.string().uuid().optional(),
  })
  .strict();

export type SaldosAFaturarQuery = z.infer<typeof saldosAFaturarQuerySchema>;

/* -------------------------------------------------------------------------- */
/* O total de várias obras                                                    */
/* -------------------------------------------------------------------------- */

/**
 * O que o painel de obras sabe de **uma** obra para somá-la (`.spec/015`,
 * decisão 95).
 */
export interface ObraNoFaturamento {
  /** O valor a faturar do cadastro. `null` é a obra sem valor: fatura livre. */
  valor: string | null;
  /** O que as notas de serviço já faturaram por ela. */
  faturado: string;
  /**
   * A obra já gerou contas a receber **pelo cadastro** — o botão dela, ou as
   * parcelas do contrato a que pertence. Pela recusa cruzada (decisão 90) ela
   * não fatura por nota, e o valor dela não é "a faturar": já virou título.
   */
  faturouPeloCadastro: boolean;
}

/**
 * O faturamento por nota de um conjunto de obras: quanto elas valem, quanto já
 * saiu em nota e quanto falta.
 *
 * Três grupos, e só o primeiro tem saldo:
 *
 * - **as que faturam por nota e têm valor** — entram nos três números;
 * - **as sem valor** (`semValor`) — fatura livre: têm faturado e não têm teto.
 *   Somá-las ao faturado de cima faria o saldo descer por notas de obras cujo
 *   valor não está na conta;
 * - **as que faturaram pelo cadastro** (`pelaGeracaoDoCadastro`) — só contadas.
 *   O valor delas já virou parcela, e entrar aqui como "a faturar" contaria a
 *   mesma receita uma segunda vez, do lado errado.
 *
 * `saldo` é `valor − faturado`, e **pode ser negativo**: uma obra faturada
 * acima do valor abate o saldo das outras. É o que a soma tem de mostrar — o
 * grupo tem a faturar o que falta de umas menos o que passou de outras.
 */
export const faturamentoPorNotaSchema = z.object({
  /** Quantas obras entram nos três números: com valor, faturando por nota. */
  obras: z.number().int().nonnegative(),
  valor: dinheiroSchema,
  faturado: dinheiroSchema,
  saldo: dinheiroSchema,
  semValor: z.object({
    obras: z.number().int().nonnegative(),
    faturado: dinheiroSchema,
  }),
  pelaGeracaoDoCadastro: z.number().int().nonnegative(),
});

export type FaturamentoPorNota = z.infer<typeof faturamentoPorNotaSchema>;

/**
 * Soma as obras nos três grupos de `faturamentoPorNotaSchema`.
 *
 * No contrato, como `confrontar`: a classificação de cada obra é regra, e a
 * tela e o teste precisam da mesma. A ordem dos testes é a da precedência —
 * a obra que faturou pelo cadastro sai antes de se olhar o valor dela.
 */
export function totalizarFaturamentoPorNota(
  obras: readonly ObraNoFaturamento[],
): FaturamentoPorNota {
  const porNota = obras.filter((obra) => !obra.faturouPeloCadastro);
  const comValor = porNota.filter((obra) => obra.valor !== null);
  const semValor = porNota.filter((obra) => obra.valor === null);

  const valor = somarDinheiro(comValor.map((obra) => obra.valor ?? '0'));
  const faturado = somarDinheiro(comValor.map((obra) => obra.faturado));

  return {
    obras: comValor.length,
    valor,
    faturado,
    saldo: subtrairDinheiro(valor, faturado),
    semValor: {
      obras: semValor.length,
      faturado: somarDinheiro(semValor.map((obra) => obra.faturado)),
    },
    pelaGeracaoDoCadastro: obras.length - porNota.length,
  };
}
